import { useState } from "react";

import { faPlus, faUser, faWarning } from "@fortawesome/pro-regular-svg-icons";

import { useCreateRole } from "src/adminPanel/queries";
import { usersApi } from "src/api/taktileApi";
import { Button } from "src/base-components/Button";
import { Input } from "src/base-components/Input";
import { Callout } from "src/design-system/Callout";
import { Icon } from "src/design-system/Icon";
import { Modal } from "src/design-system/Modal";

type Props = {
  roleName: string;
  roleTitle: string;
  isOpen: boolean;
  onClose: () => void;
};

export const AddUserToRoleModal: React.FC<Props> = ({
  roleName,
  roleTitle,
  isOpen,
  onClose,
}) => {
  const createRole = useCreateRole();
  const [email, setEmail] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const validateEmail = (email: string) => {
    if (!email) {
      return "Email is required";
    }
    // Simple email validation without complex regex
    if (!email.includes("@") || !email.includes(".")) {
      return "Please enter a valid email address";
    }
    if (!email.endsWith("@taktile.com")) {
      return "Only @taktile.com emails are allowed";
    }
    return null;
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newEmail = e.target.value;
    setEmail(newEmail);
    setError(null);

    // Clear validation error when user starts typing
    if (newEmail && error) {
      setError(null);
    }
  };

  const handleSubmit = async () => {
    const validationError = validateEmail(email);
    if (validationError) {
      setError(validationError);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response =
        await usersApi.getUserByUsernameApiV1UsersNameUsernameGet(email);
      const user = response.data;

      await createRole.mutateAsync({
        user_id: user.id,
        arguments: {},
        name: roleName,
      });

      setEmail("");
      onClose();
    } catch (err) {
      setError(
        "User not found. Please check the email address." +
          (err instanceof Error ? `: ${err.message}` : ""),
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setEmail("");
    setError(null);
    onClose();
  };

  const getRoleDescription = (roleName: string) => {
    switch (roleName) {
      case "oncall_group":
        return "Users with this role get read-only access to all customer resources and can escalate to on-call write";
      case "oncall_write":
        return "Users with this role can read and write to all customer resources";
      case "taktile_d_admin":
        return "Users with this role have administrative access to the Taktile platform";
      case "fwd_d":
        return "Users with this role have forward deployment capabilities";
      default:
        return "Users with this role have specific permissions within the system";
    }
  };

  return (
    <Modal open={isOpen} size="lg" onClose={handleClose}>
      <Modal.Header>
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-100">
            <Icon icon={faUser} size="md" variant="active" />
          </div>
          <div>
            <h2 className="font-semibold text-gray-900 text-xl">
              Add User to {roleTitle}
            </h2>
            <p className="text-gray-500 text-sm">
              Assign the {roleTitle.toLowerCase()} role to a user
            </p>
          </div>
        </div>
      </Modal.Header>
      <Modal.Content>
        <div className="space-y-6">
          {/* Role Description */}
          <Callout type="info">
            <h3>About this role</h3>
            <p>{getRoleDescription(roleName)}</p>
          </Callout>

          {/* Email Input */}
          <div className="space-y-2">
            <div className="font-medium text-gray-700 text-sm">
              Email Address <span className="text-red-500">*</span>
            </div>
            <Input
              disabled={isLoading}
              errored={!!error}
              placeholder="user@taktile.com"
              suffixIcon={error ? { icon: faWarning } : undefined}
              type="email"
              value={email}
              fullWidth
              onChange={handleEmailChange}
            />
            {error && (
              <div className="flex items-center gap-2 text-red-600 text-sm">
                <Icon icon={faWarning} size="xs" variant="error" />
                <span>{error}</span>
              </div>
            )}
            <p className="font-inter-normal-12px text-gray-500">
              Only @taktile.com email addresses are allowed for role assignment
            </p>
          </div>

          {/* Validation Info */}
          <Callout type="neutral">
            <p>What happens next?</p>
            <ul className="mt-1 space-y-1">
              <li>• The system will verify the user exists</li>
              <li>• The role will be assigned immediately</li>
              <li>• The user will have access to role-specific features</li>
            </ul>
          </Callout>
        </div>
      </Modal.Content>
      <Modal.Footer
        primaryButton={
          <Button
            disabled={!email.trim() || !!error}
            iconLeft={faPlus}
            loading={isLoading}
            size="sm"
            onClick={handleSubmit}
          >
            Add User
          </Button>
        }
        secondaryButton={
          <Button
            disabled={isLoading}
            size="sm"
            variant="secondary"
            onClick={handleClose}
          >
            Cancel
          </Button>
        }
      ></Modal.Footer>
    </Modal>
  );
};
